import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as SupplierIcon,
} from '@mui/icons-material';
import SupplierService from '../../services/supplier.service';
import { usePagination } from '../../hooks/usePagination';

const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        'pagination[page]': pagination.page + 1,
        'pagination[pageSize]': pagination.pageSize,
        sort: 'name:asc',
      };

      // Add search filter if search term exists
      if (searchTerm) {
        params['filters[$or][0][name][$containsi]'] = searchTerm;
        params['filters[$or][1][code][$containsi]'] = searchTerm;
        params['filters[$or][2][contactPerson][$containsi]'] = searchTerm;
        params['filters[$or][3][email][$containsi]'] = searchTerm;
      }

      const response = await SupplierService.getAll(params);
      const suppliersData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      setSuppliers(suppliersData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchTerm, setPagination]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        handlePageChange(0);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, handlePageChange]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await SupplierService.delete(id);
        fetchSuppliers();
      } catch (error) {
        console.error('Error deleting supplier:', error);
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Reset to first page when search changes
    setPagination({ page: 0 });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <SupplierIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Suppliers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/suppliers/create')}
        >
          New Supplier
        </Button>
      </Stack>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Search..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by name, code, contact person, or email"
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Contact Person</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No suppliers found
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map((supplier) => {
                const attrs = supplier.attributes;

                return (
                  <TableRow key={supplier.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {attrs?.code || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{attrs?.contactPerson || '-'}</TableCell>
                    <TableCell>{attrs?.email}</TableCell>
                    <TableCell>{attrs?.phone || '-'}</TableCell>
                    <TableCell>{attrs?.city || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.isActive ? 'Active' : 'Inactive'}
                        color={attrs?.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(supplier.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.pageSize}
          page={pagination.page}
          onPageChange={(_, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
        />
      )}
    </Box>
  );
};

export default SupplierList;

